import React, { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Zap, Star } from 'lucide-react';
import { UPGRADE_REASONS, PRICES } from '../../constants/subscriptionLimits';
import { createCheckoutSession } from '../../services/stripeService';

/**
 * UpgradeModal — Modal contextuel affiché quand une feature Pro est requise
 *
 * Props :
 *   isOpen      — boolean
 *   onClose     — () => void
 *   feature     — FEATURES.* string (clé de UPGRADE_REASONS)
 *   userId      — string
 *   userEmail   — string
 *   onOpenPricing — () => void  (ouvre la PricingPage complète)
 */
const UpgradeModal = memo(function UpgradeModal({
  isOpen,
  onClose,
  feature,
  userId,
  userEmail,
  onOpenPricing,
}) {
  const reason = feature ? UPGRADE_REASONS[feature] : null;
  const [loading, setLoading] = React.useState(false);

  const handleQuickUpgrade = async () => {
    setLoading(true);
    try {
      await createCheckoutSession(PRICES.annual.id, userId, userEmail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-md"
            style={{
              background: 'linear-gradient(160deg, #1a0d2e 0%, #0e0e14 100%)',
              borderRadius: '24px 24px 0 0',
              border: '1px solid rgba(139,92,246,0.3)',
              borderBottom: 'none',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {/* Glow */}
            <div
              className="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-32 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.25) 0%, transparent 70%)' }}
            />

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-full text-zinc-500 hover:text-zinc-300 transition-colors"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-6 pt-6 pb-6">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl"
                  style={{ background: 'rgba(124,58,237,0.2)' }}
                >
                  {reason?.icon || '⭐'}
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-5">
                <div className="flex items-center justify-center gap-1.5 mb-2">
                  <Crown className="w-4 h-4 text-violet-400" />
                  <span className="text-xs font-bold text-violet-400 uppercase tracking-wider">Fonctionnalité Pro</span>
                </div>
                <h2 className="text-lg font-bold text-white mb-2">
                  {reason?.title || 'Passez à Pro'}
                </h2>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {reason?.description || 'Cette fonctionnalité est disponible avec l\'abonnement Pro.'}
                </p>
              </div>

              {/* Quick stats */}
              <div
                className="flex items-center justify-around mb-5 py-3 rounded-xl"
                style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }}
              >
                <div className="text-center">
                  <div className="text-lg font-bold text-violet-300">€4.17</div>
                  <div className="text-[10px] text-zinc-500">par mois</div>
                </div>
                <div className="w-px h-8" style={{ background: 'rgba(255,255,255,0.08)' }} />
                <div className="text-center">
                  <div className="text-lg font-bold text-violet-300">-30%</div>
                  <div className="text-[10px] text-zinc-500">avec annuel</div>
                </div>
                <div className="w-px h-8" style={{ background: 'rgba(255,255,255,0.08)' }} />
                <div className="text-center">
                  <div className="text-lg font-bold text-violet-300">∞</div>
                  <div className="text-[10px] text-zinc-500">fonctionnalités</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-2.5">
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleQuickUpgrade}
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                  style={{
                    background: loading ? 'rgba(124,58,237,0.4)' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                    color: 'white',
                    boxShadow: loading ? 'none' : '0 4px 16px rgba(124,58,237,0.35)',
                  }}
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Passer Pro — €49.99/an
                    </>
                  )}
                </motion.button>

                <button
                  onClick={() => { onClose(); onOpenPricing?.(); }}
                  className="w-full py-2.5 rounded-xl text-xs font-semibold transition-colors"
                  style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}
                >
                  Voir tous les abonnements
                </button>
              </div>

              <p className="text-[10px] text-zinc-600 text-center mt-3">
                Annulation possible à tout moment · Paiement sécurisé Stripe
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
});

export default UpgradeModal;
